import React from "react";
import { Grid, Row, Col } from "react-flexbox-grid";
import activeUsers from "../images/active-users.svg";
import company from "../images/company.svg";
import trucks from "../images/trucks.svg";
import cargo from "../images/cargo.svg";
import shadow from "../images/shadow.svg";
import match from "../images/match.svg";
import chat from "../images/chat.svg";
import contractSent from "../images/contract-sent.svg";
import contractSigned from "../images/contract-signed.svg";
import { colors } from "../styles";
import CustomIcon from "../components/CustomIcon";
import Badge from "./Badge";
import BadgePlaceholder from "./BadgePlaceholder";

const orderStatuses = {
  INTERESTED: "INTERESTED",
  CONTRACT_SENT: "CONTRACT_SENT",
  CONTRACT_SIGNED: "CONTRACT_SIGNED",
};

const style = {
  h2: {
    fontFamily: "Outfit, sans-serif",
    textAlign: "center",
    fontSize: "18px",
    fontWeight: 500,
  },
};

const Title = ({ children }) => <h2 style={style.h2}>{children}</h2>;

const todayQuery = { createdAt: "today" };
const weekQuery = { createdAt: "week" };

const filterProps = {
  today: { filterQuery: todayQuery },
  week: { filterQuery: weekQuery },
};

const rideFilterProps = {
  today: { filterQuery: { startDatetimeFrom: "today" } },
  week: { filterQuery: { startDatetimeFrom: "week" } },
};

const interestedFilterQuery = { status: orderStatuses.INTERESTED };
const contractSentFilterQuery = { status: orderStatuses.CONTRACT_SENT };
const contractSignedFilterQuery = { status: orderStatuses.CONTRACT_SIGNED };

export default () => {
  const activeUsersBadge = (
    <Badge
      hasLink
      filterQuery={{ lastActiveAt: "today" }}
      resource="admin/users"
      icon={
        <CustomIcon
          src={activeUsers}
          className={{ width: 56, height: 56, marginLeft: "16px" }}
        />
      }
      title="Active users"
    />
  );

  const companiesBadge = (
    <Badge
      resource="admin/companies"
      icon={<CustomIcon src={company} className={{ marginLeft: "16px" }} />}
      title="Registered companies"
      color={colors.blueGrey["200"]}
    />
  );

  const commuteBadge = (
    <Badge
      hasLink
      resource="admin/commutes"
      icon={<CustomIcon src={trucks} className={{ marginLeft: "16px" }} />}
      title="Roster rides"
      color={colors.blue["500"]}
    />
  );

  const requestsBadge = (
    <Badge
      hasLink
      resource="admin/requests"
      icon={<CustomIcon src={cargo} className={{ marginLeft: "16px" }} />}
      title="Requests"
      color={colors.cyan["400"]}
    />
  );

  const shadowsBadge = (
    <Badge
      hasLink
      resource="admin/shadows"
      icon={<CustomIcon src={shadow} className={{ marginLeft: "16px" }} />}
      title="Empty rides"
      color={colors.indigo["200"]}
    />
  );

  const matchesBadge = (
    <Badge
      hasLink
      resource="admin/matches"
      icon={<CustomIcon src={match} className={{ marginLeft: "16px" }} />}
      title="Matches"
      color={colors.amber["500"]}
    />
  );

  // INTERESTED ORDERS

  const allInterestedOrdersBadge = (
    <Badge
      filterQuery={interestedFilterQuery}
      resource="admin/orders"
      icon={<CustomIcon src={chat} className={{ marginLeft: "16px" }} />}
      title="Interested rides"
      color={colors.lightGreen["200"]}
    />
  );

  const weekInterestedOrdersBadge = React.cloneElement(
    allInterestedOrdersBadge,
    {
      filterQuery: {
        ...weekQuery,
        ...interestedFilterQuery,
      },
    },
  );

  const todayInterestedOrdersBadge = React.cloneElement(
    allInterestedOrdersBadge,
    {
      filterQuery: {
        ...todayQuery,
        ...interestedFilterQuery,
      },
    },
  );

  // CONTRACT SENT ORDERS

  const allContractSentOrdersBadge = React.cloneElement(
    <Badge
      filterQuery={contractSentFilterQuery}
      resource="admin/orders"
      icon={
        <CustomIcon src={contractSent} className={{ marginLeft: "16px" }} />
      }
      title="Sent contracts"
      color={colors.teal["500"]}
    />,
  );

  const todayContractSentOrdersBadge = React.cloneElement(
    allContractSentOrdersBadge,
    {
      filterQuery: {
        ...todayQuery,
        ...contractSentFilterQuery,
      },
    },
  );

  const weekContractSentOrdersBadge = React.cloneElement(
    allContractSentOrdersBadge,
    {
      filterQuery: {
        ...weekQuery,
        ...contractSentFilterQuery,
      },
    },
  );
  // CONTRACT SIGNED ORDERS

  const allContractSignedOrdersBadge = React.cloneElement(
    <Badge
      filterQuery={contractSignedFilterQuery}
      resource="admin/orders"
      icon={
        <CustomIcon src={contractSigned} className={{ marginLeft: "16px" }} />
      }
      title="Signed contracts"
      color={colors.green["600"]}
    />,
  );

  const todayContractSignedOrdersBadge = React.cloneElement(
    allContractSignedOrdersBadge,
    {
      filterQuery: {
        ...todayQuery,
        ...contractSignedFilterQuery,
      },
    },
  );

  const weekContractSignedOrdersBadge = React.cloneElement(
    allContractSignedOrdersBadge,
    {
      filterQuery: {
        ...weekQuery,
        ...contractSignedFilterQuery,
      },
    },
  );

  return (
    <Grid fluid>
      <Row around="xs">
        <Col xs={6} md={3}>
          <Title>Today</Title>
          {activeUsersBadge}
          {React.cloneElement(companiesBadge, filterProps.today)}
          {React.cloneElement(commuteBadge, rideFilterProps.today)}
          {React.cloneElement(requestsBadge, rideFilterProps.today)}
          {React.cloneElement(shadowsBadge, rideFilterProps.today)}
          {React.cloneElement(matchesBadge, {
            ...filterProps.today,
          })}
          {todayInterestedOrdersBadge}
          {todayContractSentOrdersBadge}
          {todayContractSignedOrdersBadge}
        </Col>
        <Col xs={6} md={3}>
          <Title>Last week</Title>
          <BadgePlaceholder />
          {React.cloneElement(companiesBadge, filterProps.week)}
          {React.cloneElement(commuteBadge, rideFilterProps.week)}
          {React.cloneElement(requestsBadge, rideFilterProps.week)}
          {React.cloneElement(shadowsBadge, rideFilterProps.week)}
          {React.cloneElement(matchesBadge, {
            ...filterProps.week,
          })}
          {weekInterestedOrdersBadge}
          {weekContractSentOrdersBadge}
          {weekContractSignedOrdersBadge}
        </Col>
        <Col xs={6} md={3}>
          <Title>All time</Title>
          <BadgePlaceholder />
          {companiesBadge}
          {commuteBadge}
          {requestsBadge}
          {shadowsBadge}
          {matchesBadge}
          {allInterestedOrdersBadge}
          {allContractSentOrdersBadge}
          {allContractSignedOrdersBadge}
        </Col>
      </Row>
    </Grid>
  );
};



// WEBPACK FOOTER //
// ./src/dashboard/Dashboard.js